// Ekran 13 — Profilim.
// Yerleşim ve sınıf adları ref_files/btys-erp-13-profil.html:497-560'tan birebir:
//   .crumb · .page-h · .pgrid( sol .card[avatar-lg/pv-name/pv-role/mini-list/yetki]
//                            · sağ 4 kart: Hesap Bilgileri · Şifre · Bildirimler · Oturumlar )
//
// Mockup'ta hiçbir alan bir yere yazmıyordu; dördü de canlı uca bağlı.

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Kabuk from '../../components/Kabuk'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import { cihazAdi, goreliZaman, ipMaskesi, trTarih } from '../../lib/trFormat'
import { cihazTuru, tarayiciAdi, telMaskesi } from './bicim'
import { sifreOlc, SIFRE_ASGARI_UZUNLUK } from '../../lib/sifreGucu'
import { useOturum } from '../../lib/oturum'
import {
  oturumlar as oturumApi,
  profil as profilApi,
  type BildirimMatrisi,
  type Profil,
} from './api'

/** Hata gövdesindeki detay listesini tek satıra indirir — toast tek satır. */
function hataMetni(h: unknown): string {
  if (h instanceof ApiHatasi) {
    return h.detay?.length ? `${h.message} (${h.detay.join(' · ')})` : h.message
  }
  return 'Beklenmeyen bir hata oluştu.'
}

function basHarfler(ad: string): string {
  return ad
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

/** Rol → yetki rozetleri. Referanstaki dört rozet rolden TÜRETİLİYOR. */
function yetkiRozetleri(p: Profil): string[] {
  const liste: string[] = []
  if (p.yetkiOnay) liste.push('Beyan onayı')
  if (p.yetkiStorno) liste.push('Storno')
  if (p.rol === 'tesis_sorumlusu') {
    liste.push('Kullanıcı yönetimi')
    liste.push('Eşik düzenleme')
  }
  return liste
}

/** "Bugün 07:58 · Chrome" (ref 13:513); tarayıcı bilinmiyorsa yalnız zaman. */
function sonGirisMetni(p: Profil): string {
  const zaman = goreliZaman(p.sonGiris)
  const tarayici = tarayiciAdi(p.sonGirisCihaz)
  return tarayici ? `${zaman} · ${tarayici}` : zaman
}

// ------------------------------------------------------------ hesap bilgileri

function HesapBilgileri({ p }: { p: Profil }) {
  const kuyruk = useQueryClient()
  const { tazele } = useOturum()

  const [ad, setAd] = useState(p.ad)
  const [unvan, setUnvan] = useState(p.unvan ?? '')
  const [tel, setTel] = useState(p.tel ?? '')

  useEffect(() => {
    setAd(p.ad)
    setUnvan(p.unvan ?? '')
    setTel(p.tel ?? '')
  }, [p.ad, p.unvan, p.tel])

  const kaydet = useMutation({
    mutationFn: () =>
      profilApi.guncelle({ ad, unvan: unvan || null, tel: tel || null, dil: 'tr' }),
    onSuccess: async (yeni) => {
      kuyruk.setQueryData(['profil'], yeni)
      // Kabuktaki ad/avatar da tazelensin; yoksa üst barda eski ad kalır.
      await tazele()
      toast('Hesap bilgileri güncellendi')
    },
    onError: (h) => toast(hataMetni(h)),
  })

  return (
    <div className="card">
      <div className="sect-t">Hesap Bilgileri</div>
      <div style={{ padding: 12 }}>
        <div className="fgrid">
          <div className="fld">
            <label htmlFor="pf-ad">Ad Soyad</label>
            <input id="pf-ad" className="in" value={ad} onChange={(e) => setAd(e.target.value)} />
          </div>
          <div className="fld">
            <label htmlFor="pf-unvan">Ünvan</label>
            <input
              id="pf-unvan"
              className="in"
              value={unvan}
              onChange={(e) => setUnvan(e.target.value)}
            />
          </div>
          <div className="fld">
            <label htmlFor="pf-eposta">E-posta</label>
            <input id="pf-eposta" className="in" value={p.eposta} readOnly />
            <div className="fld-hint">
              E-posta oturum kimliğidir; değişimi Ayarlar → Kullanıcılar'dan yapılır.
            </div>
          </div>
          <div className="fld">
            <label htmlFor="pf-tel">Telefon</label>
            <input
              id="pf-tel"
              className="in mono"
              value={tel}
              onChange={(e) => setTel(e.target.value)}
            />
          </div>
          <div className="fld">
            <label htmlFor="pf-dil">Dil</label>
            <select id="pf-dil" className="in" value="tr" disabled>
              <option value="tr">Türkçe</option>
            </select>
            <div className="fld-hint">v1'de tek dil (§12).</div>
          </div>
          <div className="fld">
            <label htmlFor="pf-tz">Saat dilimi</label>
            <select id="pf-tz" className="in" value="ist" disabled>
              <option value="ist">Europe/Istanbul (UTC+3)</option>
            </select>
            <div className="fld-hint">Tesis ayarı — Ayarlar → Genel.</div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
          <button
            className="btn btn-pri btn-sm"
            type="button"
            disabled={kaydet.isPending}
            onClick={() => kaydet.mutate()}
          >
            Kaydet
          </button>
        </div>
      </div>
    </div>
  )
}

// ------------------------------------------------------------------- şifre

function SifreDegistir({ p }: { p: Profil }) {
  const kuyruk = useQueryClient()
  const [mevcut, setMevcut] = useState('')
  const [yeni, setYeni] = useState('')
  const [tekrar, setTekrar] = useState('')

  const olcum = useMemo(() => sifreOlc(yeni), [yeni])
  const eslesiyor = tekrar.length === 0 || yeni === tekrar

  const guncelle = useMutation({
    mutationFn: () => profilApi.sifreDegistir({ mevcut, yeni }),
    onSuccess: async (sonuc) => {
      setMevcut('')
      setYeni('')
      setTekrar('')
      await kuyruk.invalidateQueries({ queryKey: ['profil'] })
      await kuyruk.invalidateQueries({ queryKey: ['oturumlar'] })
      toast(
        sonuc.kapatilanOturum > 0
          ? `Şifre güncellendi — ${sonuc.kapatilanOturum} oturum sonlandırıldı`
          : 'Şifre güncellendi',
      )
    },
    onError: (h) => toast(hataMetni(h)),
  })

  function gonder() {
    if (!mevcut) return toast('Mevcut şifrenizi girin')
    if (yeni !== tekrar) return toast('Yeni şifre tekrarı eşleşmiyor')
    if (!olcum.yeterli)
      return toast(`Şifre yeterince güçlü değil — ${olcum.eksikler.join(' · ')}`)
    guncelle.mutate()
  }

  return (
    <div className="card" style={{ marginTop: 8 }}>
      <div className="sect-t">Şifre Değiştir</div>
      <div style={{ padding: 12 }}>
        <div className="fgrid" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
          <div className="fld">
            <label htmlFor="pf-mevcut">Mevcut şifre</label>
            <input
              id="pf-mevcut"
              className="in"
              type="password"
              placeholder="••••••••"
              value={mevcut}
              onChange={(e) => setMevcut(e.target.value)}
            />
          </div>
          <div className="fld">
            <label htmlFor="pf-yeni">Yeni şifre</label>
            <input
              id="pf-yeni"
              className="in"
              type="password"
              placeholder={`En az ${SIFRE_ASGARI_UZUNLUK} karakter`}
              value={yeni}
              onChange={(e) => setYeni(e.target.value)}
            />
            <div className="pwbar">
              <i style={{ width: olcum.genislik, background: olcum.renk }} />
            </div>
            <div className="fld-hint">
              Güç: {olcum.etiket} · harf + rakam + özel karakter önerilir
            </div>
          </div>
          {/* Uyuşmazlık kaydı ENGELLİYOR (gonder() erken dönüyor), yani bu bir
              uyarı değil doğrulama hatası: şablonun `.fld.err` kalıbı
              (ref 00:566) — kırmızı kenarlık/etiket/ipucu oradan gelir.
              `.fld-hint.tesis` amber ve K13 vurgusuna ayrılmış. */}
          <div className={eslesiyor ? 'fld' : 'fld err'}>
            <label htmlFor="pf-tekrar">Yeni şifre (tekrar)</label>
            <input
              id="pf-tekrar"
              className="in"
              type="password"
              value={tekrar}
              onChange={(e) => setTekrar(e.target.value)}
            />
            {!eslesiyor && <div className="fld-hint">Tekrar eşleşmiyor.</div>}
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 4,
          }}
        >
          <span className="cell-sub">
            {p.sifreDegisimZamani
              ? `Son değişiklik: ${trTarih(p.sifreDegisimZamani)} · `
              : 'Şifreniz hiç değiştirilmedi · '}
            şifrelerin {p.sifrePolitikaGun} günde bir yenilenmesi önerilir
          </span>
          <button
            className="btn btn-pri btn-sm"
            type="button"
            disabled={guncelle.isPending}
            onClick={gonder}
          >
            Şifreyi güncelle
          </button>
        </div>
      </div>
    </div>
  )
}

// ------------------------------------------------------------- bildirimler

function Bildirimler() {
  const kuyruk = useQueryClient()
  const sorgu = useQuery({ queryKey: ['bildirimler'], queryFn: profilApi.bildirimler })

  const yaz = useMutation({
    mutationFn: profilApi.bildirimleriYaz,
    onSuccess: (yeni) => {
      kuyruk.setQueryData(['bildirimler'], yeni)
      toast('Bildirim tercihi güncellendi')
    },
    onError: (h) => toast(hataMetni(h)),
  })

  const m: BildirimMatrisi | undefined = sorgu.data

  function cevir(olay: string, kanal: string, mevcut: boolean) {
    if (!m) return
    yaz.mutate(
      m.tercihler.map((t) =>
        t.olay === olay && t.kanal === kanal ? { ...t, acik: !mevcut } : t,
      ),
    )
  }

  return (
    <div className="card" style={{ marginTop: 8 }}>
      <div className="sect-t">
        Bildirim Tercihleri
        <span className="cell-sub" style={{ marginLeft: 6 }}>
          kişisel — tesis eşikleri Ayarlar'dadır
        </span>
      </div>
      <table className="ntab">
        <thead>
          <tr>
            <th>Olay</th>
            {(m?.kanallar ?? []).map((k) => (
              <th key={k.anahtar} style={{ width: 86 }}>
                {k.ad}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(m?.olaylar ?? []).map((o) => (
            <tr key={o.anahtar}>
              <td>
                {o.ad}
                {o.aciklama && <span className="cell-sub"> · {o.aciklama}</span>}
              </td>
              {m!.kanallar.map((k) => {
                const acik =
                  m!.tercihler.find((t) => t.olay === o.anahtar && t.kanal === k.anahtar)?.acik ??
                  false
                return (
                  <td key={k.anahtar}>
                    <span
                      className={acik ? 'tgl on' : 'tgl'}
                      role="switch"
                      aria-checked={acik}
                      aria-label={`${o.ad} · ${k.ad}`}
                      tabIndex={0}
                      onClick={() => cevir(o.anahtar, k.anahtar, acik)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          cevir(o.anahtar, k.anahtar, acik)
                        }
                      }}
                    />
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="pnote">
        Tercihler saklanıyor; v1'de e-posta/SMS gönderimi <b>henüz yok</b> — uygulama içi
        uyarılar panelde görünür.
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- oturumlar

function Oturumlar() {
  const kuyruk = useQueryClient()
  const sorgu = useQuery({ queryKey: ['oturumlar'], queryFn: oturumApi.liste })

  const sonlandir = useMutation({
    mutationFn: oturumApi.sonlandir,
    onSuccess: async () => {
      await kuyruk.invalidateQueries({ queryKey: ['oturumlar'] })
      toast('Oturum sonlandırıldı — cihaz yeniden giriş ister')
    },
    onError: (h) => toast(hataMetni(h)),
  })

  const hepsi = useMutation({
    mutationFn: oturumApi.digerleriniKapat,
    onSuccess: async (s) => {
      await kuyruk.invalidateQueries({ queryKey: ['oturumlar'] })
      toast(
        s.sonlandirilan > 0
          ? `Diğer tüm oturumlar sonlandırıldı (${s.sonlandirilan}) — denetim izine işlendi`
          : 'Kapatılacak başka oturum yok',
      )
    },
    onError: (h) => toast(hataMetni(h)),
  })

  const liste = (sorgu.data ?? []).filter((o) => o.aktif)

  return (
    <div className="card" style={{ marginTop: 8 }}>
      <div className="sect-t">Aktif Oturumlar</div>
      <table className="grid">
        <thead>
          <tr>
            <th>Cihaz</th>
            <th>Konum / IP</th>
            <th>Son etkinlik</th>
            <th style={{ width: 110 }} />
          </tr>
        </thead>
        <tbody>
          {liste.length === 0 ? (
            <tr>
              <td colSpan={4} style={{ color: 'var(--faint)' }}>
                Açık oturum yok.
              </td>
            </tr>
          ) : (
            liste.map((o) => (
              <tr key={o.id}>
                <td>
                  {/* Alt satır referansta CİHAZ SINIFI (ref 13:554: "Masaüstü").
                      Başlangıç zamanı title'a taşındı: aynı satırın 3. sütunu
                      zaten son etkinliği yazıyordu, iki zaman yan yana
                      "hangisi?" sorusu üretiyor ve cihaz bilgisi hiç
                      görünmüyordu. */}
                  <div
                    className="cell-name"
                    title={`Başlangıç: ${goreliZaman(o.baslangic)}`}
                  >
                    {cihazAdi(o.cihaz)}
                    {o.buOturum && (
                      <span className="badge b-navy" style={{ marginLeft: 4 }}>
                        bu cihaz
                      </span>
                    )}
                  </div>
                  <div className="cell-sub">{cihazTuru(o.cihaz)}</div>
                </td>
                <td style={{ fontSize: 11 }}>{ipMaskesi(o.ip)}</td>
                <td style={{ fontSize: 11 }}>{goreliZaman(o.sonEtkinlik)}</td>
                <td>
                  {!o.buOturum && (
                    <button
                      className="btn btn-ghost btn-sm"
                      type="button"
                      disabled={sonlandir.isPending}
                      onClick={() => sonlandir.mutate(o.id)}
                    >
                      Sonlandır
                    </button>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <div className="grid-foot">
        <span>Şüpheli etkinlikte tüm oturumları kapatın</span>
        <button
          className="btn btn-ghost btn-sm"
          type="button"
          disabled={hepsi.isPending}
          onClick={() => hepsi.mutate()}
        >
          Diğer tüm oturumları kapat
        </button>
      </div>
    </div>
  )
}

// ------------------------------------------------------------------- sayfa

function Icerik() {
  const { cikisOnayiAc } = useOturum()
  const sorgu = useQuery({ queryKey: ['profil'], queryFn: profilApi.getir })
  const p = sorgu.data

  // `.pane` sarmalayıcısını SekmePanolari basıyor (components/Sekmeler.tsx);
  // buradan ikinci bir tane daha çıkarsa referanstaki tek katman (ref 13:497-498)
  // iç içe iki `.pane.on` oluyordu.
  if (sorgu.isLoading) {
    return (
      <>
        <div className="crumb">
          Hesap <b>/ Profilim</b>
        </div>
        <div className="card">
          <div className="insp-empty">Profil yükleniyor…</div>
        </div>
      </>
    )
  }

  if (sorgu.isError || !p) {
    return (
      <>
        <div className="crumb">
          Hesap <b>/ Profilim</b>
        </div>
        <div className="card">
          <div className="insp-empty">Profil alınamadı — {hataMetni(sorgu.error)}</div>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="crumb">
        Hesap <b>/ Profilim</b>
      </div>
      <div className="page-h">
        <h1 className="page-t">Profilim</h1>
        <div className="page-actions">
          <button className="btn btn-ghost" type="button" onClick={cikisOnayiAc}>
            Çıkış yap
          </button>
        </div>
      </div>

      <div className="pgrid">
        <div>
          <div className="card" style={{ padding: '20px 0 10px' }}>
            <div className="avatar-lg">{basHarfler(p.ad)}</div>
            <div className="pv-name">{p.ad}</div>
            <div className="pv-role">
              <span className="badge b-navy">
                <span className="dot" />
                {p.rolAdi}
              </span>
            </div>
            <div className="mini-list" style={{ padding: '12px 14px 8px' }}>
              <div className="mini-li">
                E-posta<span className="vv">{p.eposta}</span>
              </div>
              <div className="mini-li">
                {/* Özet kartı MASKELİ (ref 13:511); ham numara Hesap
                    Bilgileri formunda düzenlenebilir hâlde duruyor. */}
                Telefon<span className="vv">{telMaskesi(p.tel)}</span>
              </div>
              <div className="mini-li">
                Tesis<span className="vv">{p.iller.length ? p.iller.join(' · ') : '—'}</span>
              </div>
              <div className="mini-li">
                {/* ref 13:513 "Bugün 07:58 · Chrome" — tarayıcı eki son girişin
                    oturumundan. Tanınmayan UA'da ek DÜŞER, uydurulmaz (K32). */}
                Son giriş<span className="vv">{sonGirisMetni(p)}</span>
              </div>
              <div className="mini-li">
                Hesap açılışı<span className="vv">{trTarih(p.hesapAcilisi)}</span>
              </div>
            </div>
            <div className="insp-sec-t" style={{ padding: '4px 14px 6px' }}>
              Yetkiler
            </div>
            <div className="yetki">
              {yetkiRozetleri(p).length === 0 ? (
                <span className="cell-sub">Ek yetki tanımlı değil.</span>
              ) : (
                yetkiRozetleri(p).map((y) => (
                  <span className="badge b-navy" key={y}>
                    {y}
                  </span>
                ))
              )}
            </div>
            <div className="pnote" style={{ margin: '10px 12px 6px' }}>
              Yetkiler rol üzerinden gelir; düzenlemesi <b>Ayarlar → Kullanıcılar &amp; Roller</b>{' '}
              ekranındadır.
            </div>
          </div>
        </div>

        <div>
          <HesapBilgileri p={p} />
          <SifreDegistir p={p} />
          <Bildirimler />
          <Oturumlar />
        </div>
      </div>
    </>
  )
}

export default function ProfilSayfasi() {
  return (
    <Kabuk listeBasligi="Profilim">
      <Icerik />
    </Kabuk>
  )
}
